import { h } from 'preact'
import PropTypes from 'prop-types'
import { useRef, useEffect, useMemo, useState } from 'preact/compat'
import { EvervaultProvider, Card } from '@evervault/react'
import { useDispatch, useSelector } from 'react-redux'
import { selectEvervaultConfig } from '../../redux/selectors/evervault'
import {
  setCardFormValues,
  setCardFormComplete,
  setCardFormError,
} from '../../redux/slice/paymentSlice'
import { transactionDetailsSelector } from '../../redux/slice/transactionDetailsSlice'
import { pageStateUpdate } from '../../redux/slice/pageStateSlice'
import { DataDog } from '../../datadog'
import SkeletonLoader from '../Common/SkeletonLoader'
import Email from './Fields/Email'
import { CARD_FIELDS } from '../../constants/card'
import { getCardTheme } from './cardTheme'
import { hasCardErrors } from '../../helpers/evervault'
import { useTranslations } from '../../hooks/useTranslations'
import './styles.scss'

/**
 * How long to wait for the Evervault <Card> `onReady` before treating the
 * iframe as unavailable and falling back to the next card form type.
 */
export const EVERVAULT_READY_TIMEOUT_MS = 15000

/** Every brand the Evervault <Card> can detect (CardBrandName). */
export const EVERVAULT_CARD_BRANDS = [
  'american-express',
  'visa',
  'mastercard',
  'discover',
  'jcb',
  'diners-club',
  'unionpay',
  'maestro',
  'mir',
  'elo',
  'hipercard',
  'hiper',
]

/** `/details` prohibitedCardBrands value (lower-cased) -> Evervault brand. */
const PROHIBITED_BRAND_TO_EVERVAULT = {
  visa: 'visa',
  mastercard: 'mastercard',
  maestro: 'maestro',
  amex: 'american-express',
}

/**
 * Turn the `/details` blocklist (`prohibitedCardBrands`) into the Evervault
 * allowlist (`acceptedBrands`). Returns `undefined` when nothing relevant is
 * prohibited so Evervault accepts every brand, matching the manual form.
 * `GiftCard` (Omnio) has no Evervault brand and is ignored here.
 *
 * @param {string[]} [prohibitedCardBrands]
 * @returns {string[] | undefined}
 */
export const getAcceptedBrands = prohibitedCardBrands => {
  const prohibited = new Set(
    (prohibitedCardBrands ?? [])
      .map(brand => PROHIBITED_BRAND_TO_EVERVAULT[brand?.toLowerCase?.()])
      .filter(Boolean),
  )

  if (!prohibited.size) return undefined

  return EVERVAULT_CARD_BRANDS.filter(brand => !prohibited.has(brand))
}

export const buildCardTranslations = l10n => ({
  number: {
    label: l10n.t('Card number'),
    placeholder: '',
    errors: {
      invalid: l10n.t('Please enter a card number'),
      unsupportedBrand: l10n.t(
        'Sorry, this card is not accepted, please use a different card',
      ),
    },
  },
  name: {
    label: l10n.t('Name on card'),
    placeholder: '',
    errors: {
      invalid: l10n.t('Please enter the name as it appears on the card'),
    },
  },
  expiry: {
    label: l10n.t('Expiry date'),
    placeholder: '',
    errors: {
      invalid: l10n.t('Please enter the expiry date as it appears on the card'),
    },
  },
  cvc: {
    label: l10n.t('Security code'),
    placeholder: '',
    errors: {
      invalid: l10n.t(
        'Please enter the security code as it appears on the card',
      ),
    },
  },
  general: {
    errors: { invalid: l10n.t('GENERAL_ERROR') },
  },
})

const EvervaultCardForm = ({
  register,
  errors,
  theme,
  isMotoTransaction = false,
  hideCardHolderName = false,
}) => {
  const dispatch = useDispatch()
  const l10n = useTranslations()
  const { teamId, appId } = useSelector(selectEvervaultConfig)
  const { prohibitedCardBrands, userEmail } = useSelector(
    transactionDetailsSelector,
  )
  const cardRef = useRef(null)
  const [isCardReady, setIsCardReady] = useState(false)

  const translations = useMemo(() => buildCardTranslations(l10n), [l10n])
  const cardTheme = useMemo(() => getCardTheme(theme), [theme])
  const acceptedBrands = useMemo(
    () => getAcceptedBrands(prohibitedCardBrands),
    [prohibitedCardBrands],
  )
  const fields = useMemo(
    () =>
      hideCardHolderName
        ? ['number', 'expiry', 'cvc']
        : ['number', 'name', 'expiry', 'cvc'],
    [hideCardHolderName],
  )

  useEffect(() => {
    const onPayRequest = () => cardRef.current?.validate?.()
    window.addEventListener('opp-pay-request', onPayRequest)
    return () => window.removeEventListener('opp-pay-request', onPayRequest)
  }, [])

  /**
   * The Evervault iframe failed (SDK `onError`) or never became ready (blocked
   * by network/CSP, so neither onReady nor onError arrives). Mark Evervault as
   * failed: selectCardFormType then moves the page to the next form type —
   * the PCI iframe when `isPciEnabled`, otherwise the default form.
   */
  const failEvervault = reason => {
    DataDog.error('Evervault card form unavailable, falling back to PCI iframe', { reason })
    dispatch(pageStateUpdate({ isEvervaultFailed: true }))
  }

  useEffect(() => {
    if (isCardReady) return undefined

    const timer = setTimeout(
      () => failEvervault('ready-timeout'),
      EVERVAULT_READY_TIMEOUT_MS,
    )
    return () => clearTimeout(timer)
  }, [isCardReady])

  const handleChange = payload => {
    dispatch(setCardFormError(hasCardErrors(payload.errors)))
    dispatch(setCardFormValues(payload))
    dispatch(setCardFormComplete(payload.isComplete && payload.isValid))
  }

  return (
    <EvervaultProvider teamId={teamId} appId={appId}>
      <h3 className="card-details__header medium nospace">
        {l10n.t('Card details')}
      </h3>
      <div className="card-form-fields">
        {!isCardReady && <SkeletonLoader count={4} />}
        <div className={isCardReady ? '' : 'card-form-fields__hidden'}>
          <Card
            ref={cardRef}
            translations={translations}
            theme={cardTheme}
            onChange={handleChange}
            onReady={() => setIsCardReady(true)}
            onError={() => failEvervault('sdk-error')}
            acceptedBrands={acceptedBrands}
            icons
            redactCVC
            fields={fields}
          />
        </div>
      </div>
      {!isMotoTransaction && !userEmail && register && (
        <Email
          register={register}
          errors={errors}
          label={l10n.t('Card holder email address')}
          type={CARD_FIELDS.email}
        />
      )}
    </EvervaultProvider>
  )
}

EvervaultCardForm.propTypes = {
  register: PropTypes.func,
  errors: PropTypes.object,
  theme: PropTypes.string,
  isMotoTransaction: PropTypes.bool,
  hideCardHolderName: PropTypes.bool,
}

export default EvervaultCardForm
